import React, { useState } from 'react'
import educationData from './educationData'
import { useAuth } from '../contexts/AuthContext'
import PreviewWall from './PreviewWall'
import { getUserProfile, updateUserProfile } from '../services/userDataService'

const styles = {
  container: {
    backgroundColor: '#010714',
    minHeight: '100vh',
    display: 'flex'
  },
  sidebar: {
    width: '320px',
    flexShrink: 0,
    backgroundColor: '#0d1526',
    borderRight: '1px solid #1b2438',
    overflowY: 'auto',
    height: '100vh',
    position: 'sticky',
    top: 0,
    boxSizing: 'border-box'
  },
  sidebarHeader: {
    padding: '24px 20px 18px',
    borderBottom: '1px solid #1b2438'
  },
  title: {
    fontSize: '20px',
    fontWeight: '800',
    letterSpacing: '-0.01em',
    color: '#f1f5f9',
    margin: 0
  },
  subtitle: {
    fontSize: '12.5px',
    color: '#8b97ab',
    margin: '4px 0 0 0'
  },
  sidebarContent: {
    padding: '10px 10px 24px'
  },
  moduleSection: {
    marginBottom: '2px'
  },
  moduleHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '10px 10px',
    borderRadius: '10px',
    fontSize: '13.5px',
    fontWeight: '600',
    color: '#cbd5e1',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    transition: 'background-color 0.15s ease',
    userSelect: 'none'
  },
  moduleHeaderActive: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '10px 10px',
    borderRadius: '10px',
    fontSize: '13.5px',
    fontWeight: '600',
    color: '#f1f5f9',
    backgroundColor: 'rgba(59, 130, 246, 0.10)',
    cursor: 'pointer',
    transition: 'background-color 0.15s ease',
    userSelect: 'none'
  },
  moduleIcon: {
    fontSize: '15px'
  },
  lessonList: {
    margin: '2px 0 8px 15px',
    borderLeft: '1px solid #1b2438',
    paddingLeft: '8px',
    display: 'flex',
    flexDirection: 'column',
    gap: '1px'
  },
  lessonItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '9px',
    padding: '7px 8px',
    borderRadius: '8px',
    fontSize: '13px',
    color: '#94a3b8',
    cursor: 'pointer',
    transition: 'all 0.15s ease'
  },
  lessonItemActive: {
    display: 'flex',
    alignItems: 'center',
    gap: '9px',
    padding: '7px 8px',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: '600',
    color: '#60a5fa',
    backgroundColor: 'rgba(59, 130, 246, 0.10)',
    cursor: 'pointer',
    transition: 'all 0.15s ease'
  },
  content: {
    flex: 1,
    padding: '32px',
    overflowY: 'auto',
    height: '100vh',
    boxSizing: 'border-box'
  },
  contentCard: {
    backgroundColor: '#0d1526',
    border: '1px solid #1b2438',
    borderRadius: '14px',
    padding: '32px 36px 28px',
    maxWidth: '820px',
    margin: '0 auto'
  },
  contentHeader: {
    marginBottom: '24px',
    paddingBottom: '20px',
    borderBottom: '1px solid #1b2438'
  },
  contentEyebrow: {
    fontSize: '11px',
    fontWeight: '600',
    letterSpacing: '0.09em',
    textTransform: 'uppercase',
    color: '#5b6678',
    marginBottom: '8px'
  },
  contentTitle: {
    fontSize: '26px',
    fontWeight: '800',
    letterSpacing: '-0.02em',
    color: '#f1f5f9',
    margin: 0,
    lineHeight: 1.2
  },
  contentText: {
    color: '#cfd8e3',
    fontSize: '14.5px',
    lineHeight: '1.7',
    whiteSpace: 'pre-line'
  },
  welcomeContent: {
    textAlign: 'center',
    color: '#94a3b8',
    fontSize: '15px',
    marginTop: '15vh'
  },
  navBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '7px',
    padding: '10px 18px',
    borderRadius: '9px',
    border: '1px solid #26354f',
    background: 'transparent',
    color: '#94a3b8',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'all 0.15s ease'
  },
  navBtnPrimary: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '7px',
    padding: '10px 20px',
    borderRadius: '9px',
    border: 'none',
    background: '#16a34a',
    color: '#ffffff',
    fontSize: '13px',
    fontWeight: '700',
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'all 0.15s ease',
    boxShadow: '0 2px 10px rgba(22, 163, 74, 0.25)'
  },
  supportLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 11px',
    borderRadius: '8px',
    border: '1px solid #26354f',
    background: 'transparent',
    color: '#8b97ab',
    fontSize: '11.5px',
    fontWeight: '600',
    textDecoration: 'none',
    transition: 'all 0.15s ease'
  },
  navBtnNext: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '7px',
    padding: '10px 20px',
    borderRadius: '9px',
    border: 'none',
    background: '#2563eb',
    color: '#ffffff',
    fontSize: '13px',
    fontWeight: '700',
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'all 0.15s ease',
    boxShadow: '0 2px 10px rgba(37, 99, 235, 0.25)'
  }
}

// Flat, ordered [moduleId, lessonId] list — drives totals and prev/next nav.
const ALL_LESSONS = Object.entries(educationData)
  .sort(([a], [b]) => parseInt(a) - parseInt(b))
  .flatMap(([mId, m]) =>
    Object.keys(m.lessons)
      .sort((a, b) => parseInt(a) - parseInt(b))
      .map(lId => [parseInt(mId), parseInt(lId)])
  )
const TOTAL_LESSONS = ALL_LESSONS.length

// One accent per module — flows through the sidebar tile, lesson eyebrow and
// in-content section headers so each module has its own identity.
const MODULE_ACCENTS = {
  1: '#60a5fa',
  2: '#34d399',
  3: '#f59e0b',
  4: '#a78bfa',
  5: '#22d3ee',
  6: '#ec4899',
  7: '#fbbf24'
}
const moduleAccent = (id) => MODULE_ACCENTS[id] || '#60a5fa'

// Records the moment the whole course was finished. Lives in the same JSONB blob
// as the lesson ticks, underscore-prefixed so it can never collide with a
// "moduleId-lessonId" key or be picked up by the doneCount filter.
const COURSE_KEY = '_completedAt'
const MODULE_IDS = Object.keys(educationData).map(Number).sort((a, b) => a - b)
const MODULE_COUNT = MODULE_IDS.length

// Seal ring geometry: one arc per module, sharing the circle's circumference.
const SEAL_R = 52
const SEAL_C = 2 * Math.PI * SEAL_R
const SEAL_PITCH = SEAL_C / MODULE_COUNT
const SEAL_DASH = SEAL_PITCH - 7 // 7px gap between arcs
const TICK_LEN = 60

// Counts from zero to `end` after `delay` ms. Used for the completion stats.
function CountUp({ end, delay = 0, duration = 900 }) {
  const [n, setN] = useState(0)
  React.useEffect(() => {
    let raf = null
    let start = null
    const step = (t) => {
      if (start === null) start = t
      const p = Math.min((t - start) / duration, 1)
      setN(Math.round(end * (1 - Math.pow(1 - p, 3))))
      if (p < 1) raf = requestAnimationFrame(step)
    }
    const timer = setTimeout(() => { raf = requestAnimationFrame(step) }, delay)
    return () => { clearTimeout(timer); if (raf) cancelAnimationFrame(raf) }
  }, [end, delay, duration])
  return <>{n}</>
}

// Ring of module-accent arcs drawing themselves in order, then the tick strokes on.
function CompletionSeal() {
  return (
    <div style={{ position: 'relative', width: '128px', height: '128px', margin: '0 auto 22px' }}>
      <div className="edu-seal-glow" />
      <svg width="128" height="128" viewBox="0 0 120 120" style={{ position: 'relative', display: 'block', transform: 'rotate(-90deg)' }}>
        <circle cx="60" cy="60" r={SEAL_R} fill="none" stroke="#16203a" strokeWidth="4" />
        {MODULE_IDS.map((mId, i) => (
          <circle
            key={mId}
            cx="60" cy="60" r={SEAL_R}
            fill="none"
            stroke={moduleAccent(mId)}
            strokeWidth="4"
            strokeLinecap="round"
            className="edu-seal-arc"
            style={{
              strokeDasharray: `${SEAL_DASH} ${SEAL_C - SEAL_DASH}`,
              strokeDashoffset: -i * SEAL_PITCH,
              animationDelay: `${i * 0.1}s`
            }}
          />
        ))}
        <path
          d="M40 61 L53 74 L80 45"
          fill="none"
          stroke="#34d399"
          strokeWidth="6"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="edu-seal-tick"
          style={{ transform: 'rotate(90deg)', transformOrigin: '60px 60px' }}
        />
      </svg>
    </div>
  )
}

export default function Education() {
  const { user, hasAccess, hadTrial, loading: authLoading } = useAuth()
  const isLocked = !authLoading && !hasAccess('education')
  const [selectedModule, setSelectedModule] = useState(null)
  const [selectedLesson, setSelectedLesson] = useState(null)
  const [expandedModules, setExpandedModules] = useState({})
  const contentRef = React.useRef(null)

  // Per-lesson completion, stored in Supabase (user_profiles.education_progress).
  const [eduProgress, setEduProgress] = useState({})
  React.useEffect(() => {
    if (!user?.id) return
    let cancelled = false
    getUserProfile(user.id).then(({ data }) => {
      if (!cancelled) setEduProgress(data?.education_progress || {})
    })
    return () => { cancelled = true }
  }, [user?.id])

  // Celebrations. Both only ever fire from a user tick below, never from the
  // initial profile load, so nothing pops on arrival at an already-finished course.
  const [celebrating, setCelebrating] = useState(false)
  const [moduleToast, setModuleToast] = useState(null)
  const [justTicked, setJustTicked] = useState(null)

  React.useEffect(() => {
    if (!moduleToast || moduleToast.leaving) return
    const t = setTimeout(() => setModuleToast(prev => (prev ? { ...prev, leaving: true } : null)), 3400)
    return () => clearTimeout(t)
  }, [moduleToast])
  React.useEffect(() => {
    if (!moduleToast || !moduleToast.leaving) return
    const t = setTimeout(() => setModuleToast(null), 320)
    return () => clearTimeout(t)
  }, [moduleToast])

  const isDone = (m, l) => !!eduProgress[`${m}-${l}`]
  const setDone = (m, l, val) => {
    const key = `${m}-${l}`
    if (!!eduProgress[key] === !!val) return
    const prev = eduProgress
    const next = { ...prev }
    if (val) next[key] = true
    else delete next[key]

    if (val) {
      setJustTicked(key)
      const total = ALL_LESSONS.filter(([mm, ll]) => !!next[`${mm}-${ll}`]).length
      const siblings = Object.keys(educationData[m].lessons).map(Number)
      if (total === TOTAL_LESSONS && !prev[COURSE_KEY]) {
        // Stamped in the same write as the tick, so it can't half-save.
        next[COURSE_KEY] = new Date().toISOString()
        setCelebrating(true)
      } else if (siblings.every(ll => !!next[`${m}-${ll}`])) {
        setModuleToast({ mId: m })
      }
    }

    setEduProgress(next) // optimistic
    if (user?.id) {
      updateUserProfile(user.id, { education_progress: next }).then(({ error }) => {
        if (error) setEduProgress(prev) // rollback on failure, celebration included
      })
    }
  }
  const doneCount = ALL_LESSONS.filter(([m, l]) => isDone(m, l)).length
  const pct = TOTAL_LESSONS > 0 ? Math.round((doneCount / TOTAL_LESSONS) * 100) : 0
  const courseComplete = TOTAL_LESSONS > 0 && doneCount === TOTAL_LESSONS

  const flatIndex = ALL_LESSONS.findIndex(([m, l]) => m === selectedModule && l === selectedLesson)
  const goToFlat = (i) => {
    if (i < 0 || i >= ALL_LESSONS.length) return
    const [m, l] = ALL_LESSONS[i]
    setExpandedModules({ [m]: true })
    setSelectedModule(m)
    setSelectedLesson(l)
  }
  const completeAndContinue = () => {
    setDone(selectedModule, selectedLesson, true)
    if (flatIndex < ALL_LESSONS.length - 1) goToFlat(flatIndex + 1)
  }

  // New lesson → start reading from the top.
  React.useEffect(() => {
    if (contentRef.current) contentRef.current.scrollTop = 0
  }, [selectedModule, selectedLesson])

  const handleModuleClick = (moduleId) => {
    if (expandedModules[moduleId]) {
      // If clicking on already expanded module, collapse it
      setExpandedModules(prev => ({ ...prev, [moduleId]: false }))
      if (selectedModule === moduleId) {
        setSelectedModule(null)
        setSelectedLesson(null)
      }
    } else {
      // Expand this module and collapse others
      setExpandedModules({ [moduleId]: true })
      setSelectedModule(moduleId)
      // Auto-select first lesson
      const firstLessonId = Object.keys(educationData[moduleId].lessons).sort((a, b) => parseInt(a) - parseInt(b))[0]
      setSelectedLesson(parseInt(firstLessonId))
    }
  }

  const handleLessonClick = (moduleId, lessonId) => {
    setSelectedModule(moduleId)
    setSelectedLesson(parseInt(lessonId))
  }

  const currentModule = selectedModule ? educationData[selectedModule] : null
  const currentLesson = currentModule && selectedLesson ? currentModule.lessons[selectedLesson] : null

  // Same wall the mobile page shows, so a locked user sees one paywall design
  // across devices. The router excludes this page from its global PreviewOverlay.
  if (isLocked) {
    return (
      <div style={{ ...styles.container, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
        <div style={{ width: '100%', maxWidth: '520px' }}>
          <PreviewWall
            title="Learn to beat the bookmakers"
            features={['Step-by-step betting strategy modules', 'EV, arbitrage, middles & bonus explained', 'Bankroll management & staking guides']}
            planName="Premium"
            accent="#60a5fa"
            hadTrial={hadTrial}
          />
        </div>
      </div>
    )
  }

  return (
    <div className="education-container" style={styles.container}>
      <style>{`
        /* Global body scrollbar styling */
        body::-webkit-scrollbar,
        html::-webkit-scrollbar {
          width: 6px;
          height: 6px;
        }
        body::-webkit-scrollbar-track,
        html::-webkit-scrollbar-track {
          background: #010714;
        }
        body::-webkit-scrollbar-thumb,
        html::-webkit-scrollbar-thumb {
          background: transparent;
          border-radius: 3px;
          transition: background 0.2s;
        }
        body:hover::-webkit-scrollbar-thumb,
        html:hover::-webkit-scrollbar-thumb {
          background: #475569;
        }
        body::-webkit-scrollbar-thumb:hover,
        html::-webkit-scrollbar-thumb:hover {
          background: #64748b !important;
        }

        /* Custom scrollbar for hover-only visibility */
        .education-scrollable::-webkit-scrollbar,
        .education-container::-webkit-scrollbar {
          width: 6px;
          height: 6px;
        }
        .education-scrollable::-webkit-scrollbar-track,
        .education-container::-webkit-scrollbar-track {
          background: #010714;
        }
        .education-scrollable::-webkit-scrollbar-thumb,
        .education-container::-webkit-scrollbar-thumb {
          background: transparent;
          border-radius: 3px;
          transition: background 0.2s;
        }
        .education-scrollable:hover::-webkit-scrollbar-thumb,
        .education-container:hover::-webkit-scrollbar-thumb {
          background: #475569;
        }
        .education-scrollable::-webkit-scrollbar-thumb:hover,
        .education-container::-webkit-scrollbar-thumb:hover {
          background: #64748b !important;
        }

        /* Firefox */
        body,
        html {
          scrollbar-width: thin;
          scrollbar-color: transparent #010714;
        }
        body:hover,
        html:hover {
          scrollbar-color: #475569 #010714;
        }
        .education-scrollable,
        .education-container {
          scrollbar-width: thin;
          scrollbar-color: transparent #010714;
        }
        .education-scrollable:hover,
        .education-container:hover {
          scrollbar-color: #475569 #010714;
        }

        /* Original Education CSS */
        .edu-header {
          font-size: 19px;
          font-weight: 800;
          letter-spacing: -0.01em;
          color: #f1f5f9;
          margin: 1.4em 0 0.5em 0;
          padding-left: 12px;
          border-left: 3px solid var(--edu-accent, #60a5fa);
          line-height: 1.3;
        }
        .edu-bold {
          font-weight: 600;
          color: #f1f5f9;
        }
        .edu-link {
          color: var(--edu-accent, #60a5fa);
          font-weight: 600;
          text-decoration: underline;
          text-underline-offset: 2px;
          cursor: pointer;
        }
        .edu-content img {
          border: 1px solid #35486b;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
          max-width: 100%;
          height: auto;
        }
        .edu-roadmap-fill {
          animation: eduRoadmapFill 1.5s cubic-bezier(0.4, 0, 0.2, 1) both;
        }
        .edu-roadmap-phase {
          animation: eduRoadmapPhase 0.5s ease both;
        }
        .edu-roadmap-phase:nth-child(1) { animation-delay: 0.05s; }
        .edu-roadmap-phase:nth-child(2) { animation-delay: 0.4s; }
        .edu-roadmap-phase:nth-child(3) { animation-delay: 0.75s; }
        .edu-roadmap-phase:nth-child(4) { animation-delay: 1.1s; }
        @keyframes eduRoadmapFill {
          from { transform: scaleX(0); }
          to { transform: scaleX(1); }
        }
        @keyframes eduRoadmapPhase {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }

        /* Completion seal */
        .edu-seal-arc {
          animation: eduSealArc 0.55s cubic-bezier(0.4, 0, 0.2, 1) both;
        }
        @keyframes eduSealArc {
          from { stroke-dasharray: 0 ${SEAL_C}; }
          to { stroke-dasharray: ${SEAL_DASH} ${SEAL_C - SEAL_DASH}; }
        }
        .edu-seal-tick {
          stroke-dasharray: ${TICK_LEN};
          animation: eduSealTick 0.45s cubic-bezier(0.65, 0, 0.35, 1) 0.85s both;
        }
        @keyframes eduSealTick {
          from { stroke-dashoffset: ${TICK_LEN}; }
          to { stroke-dashoffset: 0; }
        }
        .edu-seal-glow {
          position: absolute;
          inset: -18px;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(52,211,153,0.20), transparent 68%);
          opacity: 0;
          animation: eduSealGlow 0.9s ease 1.1s both;
        }
        @keyframes eduSealGlow {
          from { opacity: 0; transform: scale(0.7); }
          to { opacity: 1; transform: scale(1); }
        }

        /* Completion overlay */
        .edu-celebrate-backdrop { animation: eduFadeIn 0.35s ease both; }
        .edu-celebrate-card { animation: eduCardIn 0.5s cubic-bezier(0.16, 1, 0.3, 1) both; }
        .edu-celebrate-line { animation: eduRoadmapPhase 0.5s ease both; }
        @keyframes eduFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes eduCardIn {
          from { opacity: 0; transform: translateY(14px) scale(0.97); }
          to { opacity: 1; transform: none; }
        }

        /* Module completion toast */
        .edu-module-toast { animation: eduToastIn 0.4s cubic-bezier(0.16, 1, 0.3, 1) both; }
        .edu-module-toast-out { animation: eduToastOut 0.3s ease both; }
        @keyframes eduToastIn {
          from { opacity: 0; transform: translateY(12px) scale(0.97); }
          to { opacity: 1; transform: none; }
        }
        @keyframes eduToastOut {
          to { opacity: 0; transform: translateY(8px); }
        }
        .edu-toast-ring {
          stroke-dasharray: 94.25;
          stroke-dashoffset: 0;
          animation: eduToastRing 0.7s cubic-bezier(0.4, 0, 0.2, 1) 0.15s both;
        }
        @keyframes eduToastRing {
          from { stroke-dashoffset: 94.25; }
          to { stroke-dashoffset: 0; }
        }

        /* Lesson tick + progress bar */
        .edu-tick-pop { animation: eduTickPop 0.32s cubic-bezier(0.34, 1.56, 0.64, 1) both; }
        @keyframes eduTickPop {
          0% { opacity: 0; transform: scale(0.4); }
          60% { opacity: 1; transform: scale(1.15); }
          100% { opacity: 1; transform: scale(1); }
        }
        .edu-bar-sweep {
          position: absolute;
          inset: 0;
          opacity: 0;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.45), transparent);
          animation: eduBarSweep 0.9s ease 0.1s;
        }
        @keyframes eduBarSweep {
          from { opacity: 1; transform: translateX(-100%); }
          to { opacity: 1; transform: translateX(220%); }
        }

        @media (prefers-reduced-motion: reduce) {
          .edu-roadmap-fill, .edu-roadmap-phase,
          .edu-seal-arc, .edu-seal-tick, .edu-seal-glow,
          .edu-celebrate-backdrop, .edu-celebrate-card, .edu-celebrate-line,
          .edu-module-toast, .edu-toast-ring,
          .edu-tick-pop, .edu-bar-sweep { animation: none; }
        }
      `}</style>
      {/* Sidebar */}
      <div style={styles.sidebar} className="education-scrollable">
        <div style={styles.sidebarHeader}>
          <h1 style={styles.title}>Education</h1>
          <p style={styles.subtitle}>
            Master profitable betting strategies
          </p>
          {/* Course progress */}
          <div style={{ marginTop: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '6px' }}>
              <span style={{ fontSize: '10.5px', fontWeight: '700', letterSpacing: '0.08em', textTransform: 'uppercase', color: '#5b6678' }}>
                Your progress
              </span>
              <span style={{ fontSize: '11.5px', fontWeight: '700', color: doneCount > 0 ? '#34d399' : '#5b6678', fontVariantNumeric: 'tabular-nums' }}>
                {doneCount}/{TOTAL_LESSONS} · {pct}%
              </span>
            </div>
            <div style={{ position: 'relative', height: '6px', background: '#1a2336', borderRadius: '999px', overflow: 'hidden' }}>
              <div style={{ width: `${pct}%`, height: '100%', background: '#34d399', borderRadius: '999px', transition: 'width 0.4s ease' }} />
              {/* Remounts on every change so the sweep replays as progress advances. */}
              {doneCount > 0 && <div key={doneCount} className="edu-bar-sweep" />}
            </div>
          </div>
        </div>

        <div style={styles.sidebarContent}>
          {Object.entries(educationData)
            .sort(([a], [b]) => parseInt(a) - parseInt(b))
            .map(([moduleId, module]) => {
            const mId = parseInt(moduleId)
            const lessonIds = Object.keys(module.lessons)
            const moduleDone = lessonIds.filter(l => isDone(mId, parseInt(l))).length
            const moduleComplete = moduleDone === lessonIds.length
            const isExpanded = !!expandedModules[moduleId]
            return (
            <div key={moduleId} style={styles.moduleSection}>
              {/* Module Header */}
              <div
                style={selectedModule === mId ? styles.moduleHeaderActive : styles.moduleHeader}
                onClick={() => handleModuleClick(mId)}
                onMouseEnter={(e) => {
                  if (selectedModule !== mId) e.currentTarget.style.backgroundColor = 'rgba(148, 163, 184, 0.06)'
                }}
                onMouseLeave={(e) => {
                  if (selectedModule !== mId) e.currentTarget.style.backgroundColor = 'transparent'
                }}
              >
                <span style={{
                  width: '26px', height: '26px', minWidth: '26px',
                  borderRadius: '8px',
                  background: `${moduleAccent(mId)}1f`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '13px'
                }}>{module.icon}</span>
                <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{module.title}</span>
                {moduleComplete ? (
                  <span style={{ width: '16px', height: '16px', borderRadius: '50%', background: 'rgba(52,211,153,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#34d399" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                  </span>
                ) : (
                  <span style={{ fontSize: '11px', color: '#5b6678', fontWeight: '600', fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
                    {moduleDone}/{lessonIds.length}
                  </span>
                )}
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#5b6678" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }}>
                  <polyline points="6 9 12 15 18 9"/>
                </svg>
              </div>

              {/* Lessons List */}
              {isExpanded && (
                <div style={styles.lessonList}>
                  {Object.entries(module.lessons)
                    .sort(([a], [b]) => parseInt(a) - parseInt(b))
                    .map(([lessonId, lesson], idx, entries) => {
                    const lId = parseInt(lessonId)
                    const active = selectedModule === mId && selectedLesson === lId
                    const done = isDone(mId, lId)
                    const showGroup = lesson.group && (idx === 0 || entries[idx - 1][1].group !== lesson.group)
                    return (
                    <React.Fragment key={lessonId}>
                    {showGroup && (
                      <div style={{ fontSize: '10px', fontWeight: '700', letterSpacing: '0.08em', textTransform: 'uppercase', color: '#5b6678', padding: '9px 8px 3px' }}>
                        {lesson.group}
                      </div>
                    )}
                    <div
                      style={active ? styles.lessonItemActive : styles.lessonItem}
                      onClick={() => handleLessonClick(mId, lId)}
                      onMouseEnter={(e) => {
                        if (!active) { e.currentTarget.style.color = '#f1f5f9'; e.currentTarget.style.backgroundColor = 'rgba(148, 163, 184, 0.06)' }
                      }}
                      onMouseLeave={(e) => {
                        if (!active) { e.currentTarget.style.color = '#94a3b8'; e.currentTarget.style.backgroundColor = 'transparent' }
                      }}
                    >
                      {/* Completion circle — tap to toggle */}
                      <span
                        onClick={(e) => { e.stopPropagation(); setDone(mId, lId, !done) }}
                        title={done ? 'Mark as not complete' : 'Mark as complete'}
                        style={{
                          width: '15px', height: '15px', minWidth: '15px',
                          borderRadius: '50%',
                          border: `1.5px solid ${done ? '#34d399' : '#3a4760'}`,
                          background: done ? '#34d399' : 'transparent',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          cursor: 'pointer',
                          transition: 'all 0.15s ease'
                        }}
                      >
                        {done && (
                          <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#0a0f1a" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"
                            className={justTicked === `${mId}-${lId}` ? 'edu-tick-pop' : undefined}>
                            <polyline points="20 6 9 17 4 12"/>
                          </svg>
                        )}
                      </span>
                      <span style={{ flex: 1, minWidth: 0 }}>{lesson.shortTitle || lesson.title}</span>
                    </div>
                    </React.Fragment>
                    )
                  })}
                </div>
              )}
            </div>
            )
          })}
        </div>
      </div>

      {/* Content Area */}
      <div style={styles.content} className="education-scrollable" ref={contentRef}>
        {currentLesson ? (
          <div style={styles.contentCard}>
            <div style={styles.contentHeader}>
              <div style={{ ...styles.contentEyebrow, color: moduleAccent(selectedModule) }}>
                Module {selectedModule} — {currentModule.title} · Lesson {Object.keys(currentModule.lessons).sort((a, b) => parseInt(a) - parseInt(b)).indexOf(String(selectedLesson)) + 1} of {Object.keys(currentModule.lessons).length}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                <span style={{
                  width: '40px', height: '40px', minWidth: '40px',
                  borderRadius: '11px',
                  background: `${moduleAccent(selectedModule)}1f`,
                  border: `1px solid ${moduleAccent(selectedModule)}40`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '19px'
                }}>{currentModule.icon}</span>
                <h1 style={styles.contentTitle}>
                  {currentLesson.title}
                </h1>
              </div>
            </div>
            <div
              className="edu-content"
              style={{ ...styles.contentText, '--edu-accent': moduleAccent(selectedModule) }}
              onClick={(e) => {
                const lessonLink = e.target.closest('[data-lesson]')
                if (lessonLink) {
                  const [m, l] = lessonLink.dataset.lesson.split('-').map(Number)
                  if (educationData[m] && educationData[m].lessons[l]) {
                    setExpandedModules({ [m]: true })
                    setSelectedModule(m)
                    setSelectedLesson(l)
                  }
                  return
                }
                const link = e.target.closest('[data-page]')
                if (link) window.dispatchEvent(new CustomEvent('bc-navigate', { detail: link.dataset.page }))
              }}
              dangerouslySetInnerHTML={{
                __html: currentLesson.content
                  .replace(/# (.*)/g, '<h2 class="edu-header">$1</h2>')
                  .replace(/\*\*(.*?)\*\*/g, '<strong class="edu-bold">$1</strong>')
                  .replace(/\n/g, '<br />')
              }}
            />

            {/* Lesson navigation */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '28px', paddingTop: '20px', borderTop: '1px solid #1b2438' }}>
              <button
                onClick={() => goToFlat(flatIndex - 1)}
                disabled={flatIndex <= 0}
                style={{ ...styles.navBtn, opacity: flatIndex <= 0 ? 0.4 : 1, cursor: flatIndex <= 0 ? 'default' : 'pointer' }}
                onMouseEnter={(e) => { if (flatIndex > 0) { e.currentTarget.style.color = '#f1f5f9'; e.currentTarget.style.borderColor = '#35486b' } }}
                onMouseLeave={(e) => { e.currentTarget.style.color = '#94a3b8'; e.currentTarget.style.borderColor = '#26354f' }}
              >
                ← Previous
              </button>
              <div style={{ flex: 1 }} />
              {!isDone(selectedModule, selectedLesson) ? (
                <button
                  onClick={completeAndContinue}
                  style={styles.navBtnPrimary}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#15803d' }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = '#16a34a' }}
                >
                  {flatIndex >= ALL_LESSONS.length - 1 ? 'Mark complete' : 'Mark complete & continue →'}
                </button>
              ) : flatIndex < ALL_LESSONS.length - 1 ? (
                <button
                  onClick={() => goToFlat(flatIndex + 1)}
                  style={styles.navBtnNext}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#1d4ed8' }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = '#2563eb' }}
                >
                  Next lesson →
                </button>
              ) : (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', fontSize: '13px', fontWeight: '700', color: '#34d399' }}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#34d399" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  Course complete
                </span>
              )}
            </div>
          </div>
        ) : (
          <div style={{ maxWidth: '820px', margin: '0 auto' }}>
            <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: '16px', margin: '4px 0 20px' }}>
              <div>
                <h2 style={{ color: '#f1f5f9', fontSize: '24px', fontWeight: '800', letterSpacing: '-0.02em', margin: '0 0 8px' }}>
                  Welcome to Education
                </h2>
                <p style={{ margin: 0, fontSize: '14px', color: '#8b97ab' }}>
                  {courseComplete
                    ? 'You have finished every lesson. Revisit any module below whenever you need it.'
                    : doneCount > 0
                      ? `You're ${pct}% of the way through. Pick up where you left off.`
                      : 'Work through the modules in order to learn every profitable strategy.'}
                </p>
              </div>
              <button
                onClick={() => {
                  const next = ALL_LESSONS.findIndex(([m, l]) => !isDone(m, l))
                  goToFlat(next === -1 ? 0 : next)
                }}
                style={{ ...styles.navBtnNext, flexShrink: 0 }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#1d4ed8' }}
                onMouseLeave={(e) => { e.currentTarget.style.background = '#2563eb' }}
              >
                {courseComplete ? 'Review course →' : doneCount > 0 ? 'Continue course →' : 'Start the course →'}
              </button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: '12px' }}>
              {Object.entries(educationData)
                .sort(([a], [b]) => parseInt(a) - parseInt(b))
                .map(([moduleId, module]) => {
                  const mId = parseInt(moduleId)
                  const lessonIds = Object.keys(module.lessons).sort((a, b) => parseInt(a) - parseInt(b)).map(Number)
                  const done = lessonIds.filter(l => isDone(mId, l)).length
                  const complete = done === lessonIds.length
                  const modulePct = Math.round((done / lessonIds.length) * 100)
                  const accent = moduleAccent(mId)
                  return (
                    <div
                      key={moduleId}
                      onClick={() => {
                        const firstIncomplete = lessonIds.find(l => !isDone(mId, l))
                        setExpandedModules({ [mId]: true })
                        setSelectedModule(mId)
                        setSelectedLesson(firstIncomplete !== undefined ? firstIncomplete : lessonIds[0])
                      }}
                      style={{ background: '#0d1526', border: '1px solid #1b2438', borderRadius: '12px', padding: '16px', cursor: 'pointer', transition: 'border-color 0.15s ease' }}
                      onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#35486b' }}
                      onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#1b2438' }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
                        <span style={{ width: '32px', height: '32px', minWidth: '32px', borderRadius: '9px', background: `${accent}1f`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '15px' }}>{module.icon}</span>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: '10px', fontWeight: '700', letterSpacing: '0.08em', textTransform: 'uppercase', color: '#5b6678', marginBottom: '2px' }}>Module {mId}</div>
                          <div style={{ fontSize: '13.5px', fontWeight: '700', color: '#f1f5f9', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{module.title}</div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '7px' }}>
                        <span style={{ fontSize: '11.5px', color: '#8b97ab', fontVariantNumeric: 'tabular-nums' }}>{done}/{lessonIds.length} lessons</span>
                        {complete && (
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#34d399" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12"/>
                          </svg>
                        )}
                      </div>
                      <div style={{ height: '4px', background: '#1a2336', borderRadius: '999px', overflow: 'hidden' }}>
                        <div style={{ width: `${modulePct}%`, height: '100%', background: complete ? '#34d399' : accent, borderRadius: '999px', transition: 'width 0.4s ease' }} />
                      </div>
                    </div>
                  )
                })}
            </div>
          </div>
        )}
      </div>

      {/* Module completion toast */}
      {moduleToast && (
        <div
          className={moduleToast.leaving ? 'edu-module-toast edu-module-toast-out' : 'edu-module-toast'}
          style={{
            position: 'fixed', right: '26px', bottom: '26px', zIndex: 999,
            display: 'flex', alignItems: 'center', gap: '13px',
            background: '#0d1526', border: '1px solid #1b2438', borderRadius: '12px',
            padding: '13px 20px 13px 15px', maxWidth: '330px',
            boxShadow: '0 16px 40px rgba(0, 0, 0, 0.5)'
          }}
        >
          <svg width="34" height="34" viewBox="0 0 36 36" style={{ flexShrink: 0, transform: 'rotate(-90deg)' }}>
            <circle cx="18" cy="18" r="15" fill="none" stroke="#16203a" strokeWidth="3" />
            <circle cx="18" cy="18" r="15" fill="none" stroke={moduleAccent(moduleToast.mId)} strokeWidth="3" strokeLinecap="round" className="edu-toast-ring" />
          </svg>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: '10.5px', fontWeight: '700', letterSpacing: '0.08em', textTransform: 'uppercase', color: moduleAccent(moduleToast.mId), marginBottom: '3px' }}>
              Module {moduleToast.mId} complete
            </div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#f1f5f9', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {educationData[moduleToast.mId].title}
            </div>
          </div>
        </div>
      )}

      {/* Course completion overlay */}
      {celebrating && (
        <div
          className="edu-celebrate-backdrop"
          onClick={() => setCelebrating(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 1000,
            background: 'rgba(5, 9, 16, 0.82)',
            backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px'
          }}
        >
          <div
            className="edu-celebrate-card"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#0d1526', border: '1px solid #1b2438', borderRadius: '18px',
              padding: '38px 40px 32px', width: '100%', maxWidth: '440px', textAlign: 'center',
              boxShadow: '0 24px 70px rgba(0, 0, 0, 0.55)'
            }}
          >
            <CompletionSeal />
            <div className="edu-celebrate-line" style={{ animationDelay: '1.2s', fontSize: '11px', fontWeight: '700', letterSpacing: '0.1em', textTransform: 'uppercase', color: '#34d399', marginBottom: '10px' }}>
              Course complete
            </div>
            <h2 className="edu-celebrate-line" style={{ animationDelay: '1.3s', fontSize: '25px', fontWeight: '800', letterSpacing: '-0.02em', color: '#f1f5f9', margin: '0 0 10px', lineHeight: 1.25 }}>
              You've finished every lesson
            </h2>
            <p className="edu-celebrate-line" style={{ animationDelay: '1.4s', margin: '0 0 22px', fontSize: '13.5px', lineHeight: 1.65, color: '#8b97ab' }}>
              Now you have a complete understanding of every strategy. Put it to work and start making some money!
            </p>
            <div className="edu-celebrate-line" style={{ animationDelay: '1.5s', display: 'flex', borderTop: '1px solid #1b2438', borderBottom: '1px solid #1b2438', marginBottom: '22px' }}>
              {[['Modules', MODULE_COUNT, ''], ['Lessons', TOTAL_LESSONS, ''], ['Complete', 100, '%']].map(([label, value, suffix], i) => (
                <div key={label} style={{ flex: 1, padding: '14px 4px', borderLeft: i === 0 ? 'none' : '1px solid #1b2438' }}>
                  <div style={{ fontSize: '21px', fontWeight: '800', letterSpacing: '-0.02em', color: '#f1f5f9', fontVariantNumeric: 'tabular-nums' }}>
                    <CountUp end={value} delay={1650} />{suffix}
                  </div>
                  <div style={{ fontSize: '10.5px', fontWeight: '700', letterSpacing: '0.08em', textTransform: 'uppercase', color: '#5b6678', marginTop: '3px' }}>
                    {label}
                  </div>
                </div>
              ))}
            </div>
            <div className="edu-celebrate-line" style={{ animationDelay: '1.6s', display: 'flex', gap: '10px' }}>
              <button
                onClick={() => setCelebrating(false)}
                style={{ ...styles.navBtn, flex: 1, justifyContent: 'center' }}
                onMouseEnter={(e) => { e.currentTarget.style.color = '#f1f5f9'; e.currentTarget.style.borderColor = '#35486b' }}
                onMouseLeave={(e) => { e.currentTarget.style.color = '#94a3b8'; e.currentTarget.style.borderColor = '#26354f' }}
              >
                Back to lessons
              </button>
              <button
                onClick={() => { setCelebrating(false); window.dispatchEvent(new CustomEvent('bc-navigate', { detail: 'dashboard' })) }}
                style={{ ...styles.navBtnNext, flex: 1, justifyContent: 'center' }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#1d4ed8' }}
                onMouseLeave={(e) => { e.currentTarget.style.background = '#2563eb' }}
              >
                Go to dashboard →
              </button>
            </div>
            {/* Support footer */}
            <div className="edu-celebrate-line" style={{ animationDelay: '1.7s', marginTop: '22px', paddingTop: '18px', borderTop: '1px solid #1b2438' }}>
              <div style={{ fontSize: '12.5px', color: '#8b97ab', marginBottom: '11px' }}>
                Still need help with something?
              </div>
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap' }}>
                <a
                  href="https://discord.gg/BV4dVEnx73"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={styles.supportLink}
                  onMouseEnter={(e) => { e.currentTarget.style.color = '#f1f5f9'; e.currentTarget.style.borderColor = '#35486b'; e.currentTarget.style.background = 'rgba(148, 163, 184, 0.06)' }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = '#8b97ab'; e.currentTarget.style.borderColor = '#26354f'; e.currentTarget.style.background = 'transparent' }}
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03z"/>
                  </svg>
                  Ask the team on Discord
                </a>
                <a
                  href="mailto:support@bookiecharity.com"
                  style={styles.supportLink}
                  onMouseEnter={(e) => { e.currentTarget.style.color = '#f1f5f9'; e.currentTarget.style.borderColor = '#35486b'; e.currentTarget.style.background = 'rgba(148, 163, 184, 0.06)' }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = '#8b97ab'; e.currentTarget.style.borderColor = '#26354f'; e.currentTarget.style.background = 'transparent' }}
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                    <polyline points="22,6 12,13 2,6"/>
                  </svg>
                  support@bookiecharity.com
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}